import type {
    LiveControlWriteRequestTO,
    SimulationStateNameTO,
    VehicleAttributeKeyTO as VehicleAttributeKeyTOType,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { SimulationStateNameTO as StateNames } from '@control-center/control-center-client-public-domain-simulation-ts';
import Button from 'components/actions/Button';
import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import NoData from 'components/content/NoData';
import Sidebar from 'components/navigation/Sidebar';
import type React from 'react';
import { createPortal } from 'react-dom';

import useSimulationDetail from './hooks/useSimulationDetail';

const DETAIL_POLL_INTERVAL_MS = 3000;

import SimulationProgressWidget from './SimulationProgressWidget';
import SimulationVehicleAttributes from './SimulationVehicleAttributes';
import './_styles.scss';

interface SidebarRowProps {
    label: string;
    children: React.ReactNode;
}

const SidebarRow = ({ label, children }: SidebarRowProps) => (
    <div className='SimulationDetailSidebar__row'>
        <span className='SimulationDetailSidebar__label'>{label}</span>
        <span className='SimulationDetailSidebar__value'>{children}</span>
    </div>
);

interface SidebarSectionProps {
    title: string;
    children: React.ReactNode;
    headerAction?: React.ReactNode;
}

const SidebarSection = ({ title, children, headerAction }: SidebarSectionProps) => (
    <div className='SimulationDetailSidebar__section'>
        <div className='SimulationDetailSidebar__sectionTitle'>
            <span>{title}</span>
            {headerAction}
        </div>
        <div className='SimulationDetailSidebar__sectionContent'>{children}</div>
    </div>
);

interface ActionButtonProps {
    icon: string;
    label: string;
    onClick: () => void;
    style?: 'primary' | 'danger';
    'data-testid'?: string;
}

const SidebarActionButton = ({ icon, label, onClick, style, 'data-testid': dataTestId }: ActionButtonProps) => (
    <Button style={style} onClick={onClick} data-testid={dataTestId}>
        <span className={`rioglyph ${icon}`} />
        {label}
    </Button>
);

interface Props {
    vin: string | null;
    isOpen: boolean;
    onClose: () => void;
    onStart: () => void;
    onDelete: () => void;
    onToggleConnectivity: () => void;
    onTriggerMrm: () => void;
    onWriteAttributes: (vin: string, body: LiveControlWriteRequestTO) => Promise<void>;
    onPinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
    onUnpinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
}

const getActionAvailability = (state: SimulationStateNameTO) => ({
    canStart: state === StateNames.Idle,
    canTriggerMrm: state === StateNames.Running,
    canToggleConnectivity: state !== StateNames.Completing && state !== StateNames.Completed,
    canEditAttributes: state !== StateNames.Completing && state !== StateNames.Completed,
});

export const SIDEBAR_PORTAL_ID = 'simulations-right-sidebar';

const SimulationDetailSidebar = ({
    vin,
    isOpen,
    onClose,
    onStart,
    onDelete,
    onToggleConnectivity,
    onTriggerMrm,
    onWriteAttributes,
    onPinAttribute,
    onUnpinAttribute,
}: Props) => {
    const { detail, isError, lastUpdatedAt } = useSimulationDetail(vin, DETAIL_POLL_INTERVAL_MS);
    const state = detail?.state.name;
    const isOnline = detail?.isOnline ?? false;
    const actions = state ? getActionAvailability(state) : null;

    const footer = (
        <div className='SimulationDetailSidebar__footer'>
            {actions?.canStart && (
                <SidebarActionButton icon='rioglyph-play' label='Start' onClick={onStart} style='primary' />
            )}
            {actions?.canTriggerMrm && (
                <SidebarActionButton
                    icon='rioglyph-warning-sign'
                    label='Trigger MRM'
                    onClick={onTriggerMrm}
                    style='danger'
                />
            )}
            {actions?.canToggleConnectivity && (
                <SidebarActionButton
                    icon={isOnline ? 'rioglyph-wifi-off' : 'rioglyph-wifi'}
                    label={isOnline ? 'Go offline' : 'Go online'}
                    onClick={onToggleConnectivity}
                />
            )}
            <SidebarActionButton
                icon='rioglyph-trash'
                label='Delete'
                onClick={onDelete}
                style='danger'
                data-testid='domain-sim-sidebar-delete'
            />
            {lastUpdatedAt && (
                <span className='SimulationDetailSidebar__lastUpdated' data-testid='domain-sim-last-updated'>
                    <span className='rioglyph rioglyph-refresh' />
                    {lastUpdatedAt.toLocaleTimeString()}
                </span>
            )}
        </div>
    );

    const sidebar = (
        <Sidebar
            fly
            position='right'
            closed={!isOpen}
            onClose={onClose}
            className='SimulationDetailSidebar__drawer'
            title={
                <span className='SimulationDetailSidebar__title'>
                    Simulation Detail
                    <a
                        href='https://confluence.collaborationlayer-traton.com/spaces/ACC/pages/517180442/Domain+Simulation'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='SimulationDetailSidebar__docLink'
                        title='Open documentation'
                    >
                        <span className='rioglyph rioglyph-question-sign' />
                    </a>
                </span>
            }
            footer={footer}
            data-testid='domain-sim-detail-sidebar'
        >
            <div className='SimulationDetailSidebar__content'>
                {isError && !detail ? (
                    <div className='SimulationDetailSidebar__loading'>
                        <span className='rioglyph rioglyph-warning-sign' />
                        <span>Failed to load simulation details</span>
                    </div>
                ) : !detail ? (
                    <div className='SimulationDetailSidebar__loading'>
                        <span className='rioglyph rioglyph-spinner' />
                    </div>
                ) : (
                    <>
                        <SidebarSection title='Status'>
                            <SimulationProgressWidget
                                state={detail.state.name}
                                stateDetail={detail.state}
                                isOnline={detail.isOnline}
                                numberOfSteps={detail.scenarioInfo.numberOfSteps}
                                startedAt={detail.startedAt}
                            />
                        </SidebarSection>

                        <SidebarSection title='Simulation'>
                            <SidebarRow label='VIN'>{detail.vin}</SidebarRow>
                            <SidebarRow label='Scenario'>
                                {detail.scenarioInfo.scenarioName ?? <NoData className='text-14' />}
                            </SidebarRow>
                            <SidebarRow label='Driver mode'>
                                {detail.vehicleRepresentation.hocInfo?.value?.driverMode ?? (
                                    <NoData className='text-14' />
                                )}
                            </SidebarRow>
                        </SidebarSection>

                        <SimulationVehicleAttributes
                            key={`${detail.vin}-${String(isOpen)}`}
                            vin={detail.vin}
                            vehicleRepresentation={detail.vehicleRepresentation}
                            pinnedAttributes={detail.pinnedAttributes}
                            missionId={detail.missionInfo.missionId ?? undefined}
                            canEdit={actions?.canEditAttributes ?? false}
                            onWriteAttributes={onWriteAttributes}
                            onPinAttribute={onPinAttribute}
                            onUnpinAttribute={onUnpinAttribute}
                        />

                        <SidebarSection title='Timestamps'>
                            <SidebarRow label='Started'>
                                <DateTimeOrNoData value={detail.startedAt ?? undefined} />
                            </SidebarRow>
                            <SidebarRow label='Created'>
                                <DateTimeOrNoData value={detail.createdAt} />
                            </SidebarRow>
                        </SidebarSection>
                    </>
                )}
            </div>
        </Sidebar>
    );

    const portalTarget = document.getElementById(SIDEBAR_PORTAL_ID);
    return portalTarget ? createPortal(sidebar, portalTarget) : sidebar;
};

export default SimulationDetailSidebar;
